import { useState } from "react";
import { Plus } from "lucide-react";
import { GroupHeader } from "@/components/opportunities/GroupHeader";
import { OpportunityRows } from "@/components/opportunities/OpportunityRows";
import { EmptyState } from "@/components/common/Primitives";
import { num } from "@/lib/format";

const PAGE = 8;

export const GroupedView = ({
  tree,
  columns,
  showSummary,
  sortRows,
  selection = {},
  onRefreshGroup,
  refreshingKey,
  onChanged,
}) => {
  const [state, setState] = useState({});
  const [shown, setShown] = useState({});

  const isOpen = (key, fallback) => (key in state ? state[key] : fallback);
  const toggle = (key, fallback) => setState((s) => ({ ...s, [key]: !isOpen(key, fallback) }));
  const singleType = tree.length === 1 && tree[0].models.length === 1 && tree[0].models[0].types.length === 1;

  if (tree.length === 0) {
    return (
      <EmptyState
        title="Geen voertuigen in deze selectie"
        description="Pas de filters of de merk/model-selectie aan. Er wordt nooit demo- of fictieve data getoond."
        testid="grouped-empty"
      />
    );
  }

  return (
    <div className="space-y-2" data-testid="grouped-view">
      {tree.map((make) => {
        const makeKey = `make:${make.key}`;
        const makeOpen = isOpen(makeKey, true);
        return (
          <section className="panel overflow-hidden" key={make.key}>
            <GroupHeader
              label={make.label}
              count={make.stats.count}
              stats={make.stats}
              level="make"
              open={makeOpen}
              onToggle={() => toggle(makeKey, true)}
              showSummary={showSummary}
              refreshing={refreshingKey === makeKey}
              onRefresh={() => onRefreshGroup({ make_key: make.key }, makeKey)}
              testid={`group-make-${make.key}`}
            />
            {makeOpen &&
              make.models.map((model) => {
                const modelKey = `model:${make.key}:${model.key}`;
                const modelOpen = isOpen(modelKey, true);
                return (
                  <div key={model.key}>
                    <GroupHeader
                      label={model.label}
                      count={model.stats.count}
                      stats={model.stats}
                      level="model"
                      open={modelOpen}
                      onToggle={() => toggle(modelKey, true)}
                      showSummary={showSummary}
                      refreshing={refreshingKey === modelKey}
                      onRefresh={() =>
                        onRefreshGroup({ make_key: make.key, model_key: model.key }, modelKey)
                      }
                      testid={`group-model-${make.key}-${model.key}`}
                    />
                    {modelOpen &&
                      model.types.map((type) => {
                        const typeKey = `type:${make.key}:${model.key}:${type.key}`;
                        const defaultOpen =
                          singleType ||
                          (selection.type_key === type.key &&
                            selection.model_key === model.key &&
                            selection.make_key === make.key);
                        const typeOpen = isOpen(typeKey, defaultOpen);
                        const limit = shown[typeKey] || PAGE;
                        const items = sortRows(type.items);
                        return (
                          <div key={type.key}>
                            <GroupHeader
                              label={type.label}
                              count={type.stats.count}
                              stats={type.stats}
                              level="type"
                              open={typeOpen}
                              onToggle={() => toggle(typeKey, defaultOpen)}
                              showSummary={showSummary}
                              refreshing={refreshingKey === typeKey}
                              onRefresh={() =>
                                onRefreshGroup(
                                  {
                                    make_key: make.key,
                                    model_key: model.key,
                                    type_key: type.key,
                                  },
                                  typeKey
                                )
                              }
                              testid={`group-type-${make.key}-${model.key}-${type.key}`}
                            />
                            {typeOpen && (
                              <div className="border-b border-slate-200 bg-white">
                                <OpportunityRows
                                  rows={items.slice(0, limit)}
                                  columns={columns}
                                  onChanged={onChanged}
                                  testid={`rows-${make.key}-${model.key}-${type.key}`}
                                />
                                {items.length > limit && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShown((s) => ({ ...s, [typeKey]: limit + 20 }))
                                    }
                                    data-testid={`load-more-${make.key}-${model.key}-${type.key}`}
                                    className="flex w-full items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-blue-700 transition-colors duration-150 hover:bg-blue-50"
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                    Nog {num(items.length - limit)} voertuigen in deze groep
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                );
              })}
          </section>
        );
      })}
    </div>
  );
};
